import {
  ButtonGroup,
  Loading,
  Panel,
  Tooltip,
} from "@appkit4/react-components";
import { GridCellKind } from "@glideapps/glide-data-grid";
import "@glideapps/glide-data-grid/dist/index.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useCallback, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CompanyPanel from "../components/CompanyPanel";
import toast from "../components/DismissibleToast";
import { BackButton, NextButton } from "../components/ProgressNav";
import {
  Button,
  DataEditor,
  InputNumber,
} from "../components/ReadonlyAwareInputs";
import RoundedBorder from "../components/ui/RoundedBorder";
import { AuthContext } from "../services/AuthProvider";
import {
  calculate_disallowance_subtotals,
  getReviewCalc3Values,
} from "../services/calculations/calc3";
import {
  canProgressCalculation,
  disableProgressInViewMode,
  getCurrentPeriodIsReadonly,
  getGroupData,
  getGroupID,
  getPoaIndex,
  setGroupData,
} from "../services/GroupContext";
import sqlService from "../services/sqldatabase/sqldatabase.service";
import { getIncludedAdjCaps, sortCaps } from "../utils/adjustedCapsUtil";
import { processDate, processDateJS } from "../utils/dateProcessor";
import ReviewCalcNav from "./revCalcNavigation";
import RevCalcHeader from "./ReviewCalcHeader";
dayjs.extend(utc);

const disallowed_allocation_table_rows = [
  {
    metric_id: "non_consenting_company",
    display_name: "Non-consenting company?",
    mandatory: true,
    editable: true,
    type: "boolean",
  },
  {
    metric_id: "start_date",
    display_name: "Accounting Period Start Date",
    mandatory: true,
    type: "date",
  },
  {
    metric_id: "end_date",
    display_name: "Accounting Period End Date",
    mandatory: true,
    type: "date",
  },
  {
    metric_id: "net_tax_interest_expense_gbp",
    display_name: "Net tax-interest expense / (income) - GBP",
    mandatory: true,
    type: "number_total",
  },
  {
    metric_id: "max_disallowance",
    display_name: "Disallowance allocation limit - GBP",
    mandatory: true,
    type: "number",
  },
  {
    metric_id: "disallowance_gbp",
    display_name: "Disallowance allocation - GBP",
    mandatory: true,
    editable: true,
    type: "number_total",
    default: 0,
  },
];

const disallowed_allocation_ntie_row_index =
  disallowed_allocation_table_rows.findIndex(
    (metric) => metric.metric_id === "net_tax_interest_expense_gbp",
  );
const disallowed_allocation_disallowance_row_index =
  disallowed_allocation_table_rows.findIndex(
    (metric) => metric.metric_id === "disallowance_gbp",
  );
if (
  disallowed_allocation_ntie_row_index === -1 ||
  disallowed_allocation_disallowance_row_index === -1
) {
  throw new Error(
    "Error: Disallowed allocation table is missing Available or Allocated reactivation metrics",
  );
}

const disallowed_deductions_table_rows = [
  {
    metric_id: "non_consenting_company",
    display_name: "Non-consenting company?",
    mandatory: true,
    type: "boolean",
  },
  {
    metric_id: "start_date",
    display_name: "Accounting Period Start Date",
    mandatory: true,
    type: "date",
  },
  {
    metric_id: "end_date",
    display_name: "Accounting Period End Date",
    mandatory: true,
    type: "date",
  },
  {
    metric_id: "apply_s377_3_election",
    display_name:
      "Apply s377(3) election, to override the automatic order of disallowances",
    mandatory: true,
    editable: true,
    type: "boolean",
  },
  {
    metric_id: "disallowance_gbp",
    display_name: "Total allocated disallowance for accounting period - GBP",
    mandatory: true,
    type: "number",
    default: 0,
  },
  {
    metric_id: "currency",
    display_name: "Currency",
    mandatory: true,
    type: "string",
  },
  {
    metric_id: "disallowance",
    display_name:
      "Total allocated disallowance for accounting period - Local Currency",
    mandatory: true,
    type: "number",
    calculation: (cap) => Math.round(cap.disallowance_gbp.value * cap.fx_rate),
    default: 0,
  },
  {
    metric_id: "ntlr_subtotal",
    display_name: "Non trading loan relationship debits for the period",
    mandatory: true,
    type: "number",
  },
  {
    metric_id: "ntlr_disallowance",
    display_name:
      "Disallowance allocated to non trading loan relationship debits",
    mandatory: true,
    type: "edit_number",
  },
  {
    metric_id: "ntdc_subtotal",
    display_name: "Non trading derivative contract debits for the period",
    mandatory: true,
    type: "number",
  },
  {
    metric_id: "ntdc_disallowance",
    display_name:
      "Disallowance allocated to non trading derivative contract debits",
    mandatory: true,
    type: "edit_number",
  },
  {
    metric_id: "tlr_subtotal",
    display_name: "Trading loan relationship debits for the period",
    mandatory: true,
    type: "number",
  },
  {
    metric_id: "tlr_disallowance",
    display_name: "Disallowance allocated to trading loan relationship debits",
    mandatory: true,
    type: "edit_number",
  },
  {
    metric_id: "tdc_subtotal",
    display_name: "Trading derivative contract debits for the period",
    mandatory: true,
    type: "number",
  },
  {
    metric_id: "tdc_disallowance",
    display_name:
      "Disallowance allocated to trading derivative contract debits",
    mandatory: true,
    type: "edit_number",
  },
  {
    metric_id: "ofa_subtotal",
    display_name: "Other financing arrangements debits for the period",
    mandatory: true,
    type: "number",
  },
  {
    metric_id: "ofa_disallowance",
    display_name:
      "Disallowance allocation to other financing arrangement debits",
    mandatory: true,
    type: "edit_number",
  },
  {
    metric_id: "remaining_disallowance",
    display_name: "Remaining disallowance to be allocated",
    mandatory: true,
    type: "number",
  },
];

const getDisallowedAllocationIndex = (metricId) => {
  return disallowed_allocation_table_rows.findIndex(
    (row) => row.metric_id === metricId,
  );
};

const getDisallowedDeductionsIndex = (metricId) => {
  return disallowed_deductions_table_rows.findIndex(
    (row) => row.metric_id === metricId,
  );
};

const ReviewCalc3 = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isPosting, setIsPosting] = useState(false);
  const [isErrorA, setIsErrorA] = useState("");
  const [isErrorB, setIsErrorB] = useState("");
  const [remainingDisallowanceError, setRemainingDisallowanceError] =
    useState("");
  const auth = useContext(AuthContext);
  const tenantId = useSelector((state) => state.usageInfo.tenantID);
  const isReadOnlyOrViewPoA =
    auth?.isReadOnlyUser() || getCurrentPeriodIsReadonly();
  const disableViewModeProgress = disableProgressInViewMode();

  const [savedPOAData, setSavedPOAData] = useState();
  const [savedData, setSavedData] = useState();
  const poaIndex = getPoaIndex();
  const [remainingDisallowance, setRemainingDisallowance] = useState("0");

  const [dataInTheTable, setDataInTheTable] = useState([]);
  // Setup Tables
  const [
    disallowed_allocation_table_columns,
    set_disallowed_allocation_table_columns,
  ] = useState([]);
  const [
    disallowed_allocation_table_data,
    set_disallowed_allocation_table_data,
  ] = useState([]);
  const [
    disallowed_allocation_totals_column,
    set_disallowed_allocation_totals_column,
  ] = useState([]);

  useEffect(() => {
    if (isReadOnlyOrViewPoA) return;

    disallowanceError(
      disallowed_allocation_table_data,
      5,
      4,
      setIsErrorA,
      true,
      3,
    );
  }, [disallowed_allocation_table_data]);

  const [
    disallowed_deductions_table_columns,
    set_disallowed_deductions_table_columns,
  ] = useState([]);
  const [
    disallowed_deductions_table_data,
    set_disallowed_deductions_table_data,
  ] = useState([]);

  useEffect(() => {
    const val = parseInt(remainingDisallowance?.replace(/,/g, ""));
    if (val === 0 || isReadOnlyOrViewPoA) {
      setRemainingDisallowanceError("");
    } else {
      val > 0
        ? setRemainingDisallowanceError(
            "Remaining Disallowance can not be greater than 0",
          )
        : setRemainingDisallowanceError(
            "Remaining Disallowance can not be smaller than 0",
          );
    }
  }, [remainingDisallowance]);

  const setRemainingDisallowanceForAllocation = (
    disallowedAllocationData,
    groupDisallowance = undefined,
  ) => {
    // Calculate Totals column
    const tmp_disallowed_allocation_totals_column = new Array(
      disallowedAllocationData.length,
    );
    disallowed_allocation_table_rows.forEach((metric, index) => {
      if (metric.type !== "number_total") return;

      tmp_disallowed_allocation_totals_column[index] = disallowedAllocationData[
        index
      ].reduce((acc, val) => acc + val, 0);
    });
    set_disallowed_allocation_totals_column(
      tmp_disallowed_allocation_totals_column,
    );

    const newRemainingDisallowance = (
      (groupDisallowance ?? savedPOAData?.group_disallowance) -
      tmp_disallowed_allocation_totals_column[
        disallowed_allocation_disallowance_row_index
      ]
    ).toLocaleString();
    setRemainingDisallowance(newRemainingDisallowance);
  };

  const onAllocationOfDisallowancesEdited = (disallowedAllocationData) => {
    if (!disallowedAllocationData.length || isReadOnlyOrViewPoA) {
      setIsErrorB(false);
      return;
    }

    setRemainingDisallowanceForAllocation(disallowedAllocationData);

    let temp_disallowed_deductions_data = [...disallowed_deductions_table_data];
    disallowed_deductions_table_data[
      getDisallowedDeductionsIndex("non_consenting_company")
    ]?.forEach((_, index) => {
      temp_disallowed_deductions_data[
        getDisallowedDeductionsIndex("non_consenting_company")
      ][index] =
        disallowed_allocation_table_data[
          getDisallowedAllocationIndex("non_consenting_company")
        ][index];
      temp_disallowed_deductions_data[
        getDisallowedDeductionsIndex("disallowance_gbp")
      ][index] =
        disallowed_allocation_table_data[
          getDisallowedAllocationIndex("disallowance_gbp")
        ][index];

      const cap = savedPOAData?.adjusted_caps?.find(
        (cap) =>
          cap?.company_name ===
            disallowed_deductions_table_columns[index + 1]?.title &&
          processDateJS(cap?.start_date)?.isSame(
            processDateJS(
              disallowed_deductions_table_data[
                getDisallowedDeductionsIndex("start_date")
              ][index],
            ),
          ),
      );

      if (!cap) return;

      temp_disallowed_deductions_data[
        getDisallowedDeductionsIndex("disallowance")
      ][index] = Math.round(
        disallowed_allocation_table_data[
          getDisallowedAllocationIndex("disallowance_gbp")
        ][index] * cap.fx_rate,
      );

      const newSubtotals = calculate_disallowance_subtotals(
        cap,
        Math.max(cap?.net_tax_interest_expense_gbp, 0),
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("disallowance")
        ][index],
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("disallowance_gbp")
        ][index],
      );
      if (
        !temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("apply_s377_3_election")
        ][index]
      ) {
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("ntlr_disallowance")
        ][index] = newSubtotals?.ntlr_disallowance;
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("ntdc_disallowance")
        ][index] = newSubtotals?.ntdc_disallowance;
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("tlr_disallowance")
        ][index] = newSubtotals?.tlr_disallowance;
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("tdc_disallowance")
        ][index] = newSubtotals?.tdc_disallowance;
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("ofa_disallowance")
        ][index] = newSubtotals?.ofa_disallowance;
      }
      temp_disallowed_deductions_data[
        getDisallowedDeductionsIndex("remaining_disallowance")
      ]?.forEach((_, col) => {
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("remaining_disallowance")
        ][col] =
          temp_disallowed_deductions_data[
            getDisallowedDeductionsIndex("disallowance")
          ][col] -
          temp_disallowed_deductions_data[
            getDisallowedDeductionsIndex("ntlr_disallowance")
          ][col] -
          temp_disallowed_deductions_data[
            getDisallowedDeductionsIndex("ntdc_disallowance")
          ][col] -
          temp_disallowed_deductions_data[
            getDisallowedDeductionsIndex("tlr_disallowance")
          ][col] -
          temp_disallowed_deductions_data[
            getDisallowedDeductionsIndex("tdc_disallowance")
          ][col] -
          temp_disallowed_deductions_data[
            getDisallowedDeductionsIndex("ofa_disallowance")
          ][col];
      });
    });
    set_disallowed_deductions_table_data(temp_disallowed_deductions_data);

    let error = "";
    disallowanceError(
      disallowed_deductions_table_data,
      getDisallowedDeductionsIndex("ntlr_disallowance"),
      getDisallowedDeductionsIndex("ntlr_subtotal"),
      (msg) => {
        error = msg || error;
      },
    );
    disallowanceError(
      disallowed_deductions_table_data,
      getDisallowedDeductionsIndex("ntdc_disallowance"),
      getDisallowedDeductionsIndex("ntdc_subtotal"),
      (msg) => {
        error = msg || error;
      },
    );
    disallowanceError(
      disallowed_deductions_table_data,
      getDisallowedDeductionsIndex("tlr_disallowance"),
      getDisallowedDeductionsIndex("tlr_subtotal"),
      (msg) => {
        error = msg || error;
      },
    );
    disallowanceError(
      disallowed_deductions_table_data,
      getDisallowedDeductionsIndex("tdc_disallowance"),
      getDisallowedDeductionsIndex("tdc_subtotal"),
      (msg) => {
        error = msg || error;
      },
    );
    disallowanceError(
      disallowed_deductions_table_data,
      getDisallowedDeductionsIndex("ofa_disallowance"),
      getDisallowedDeductionsIndex("ofa_subtotal"),
      (msg) => {
        error = msg || error;
      },
    );
    setIsErrorB(error);
  };

  useEffect(() => {
    if (isReadOnlyOrViewPoA) {
      setIsErrorB(false);
      return;
    }

    let error = "";
    disallowanceError(
      disallowed_deductions_table_data,
      getDisallowedDeductionsIndex("ntlr_disallowance"),
      getDisallowedDeductionsIndex("ntlr_subtotal"),
      (msg) => {
        error = msg || error;
      },
    );
    disallowanceError(
      disallowed_deductions_table_data,
      getDisallowedDeductionsIndex("ntdc_disallowance"),
      getDisallowedDeductionsIndex("ntdc_subtotal"),
      (msg) => {
        error = msg || error;
      },
    );
    disallowanceError(
      disallowed_deductions_table_data,
      getDisallowedDeductionsIndex("tlr_disallowance"),
      getDisallowedDeductionsIndex("tlr_subtotal"),
      (msg) => {
        error = msg || error;
      },
    );
    disallowanceError(
      disallowed_deductions_table_data,
      getDisallowedDeductionsIndex("tdc_disallowance"),
      getDisallowedDeductionsIndex("tdc_subtotal"),
      (msg) => {
        error = msg || error;
      },
    );
    disallowanceError(
      disallowed_deductions_table_data,
      getDisallowedDeductionsIndex("ofa_disallowance"),
      getDisallowedDeductionsIndex("ofa_subtotal"),
      (msg) => {
        error = msg || error;
      },
    );

    const sum = disallowed_deductions_table_data[
      getDisallowedDeductionsIndex("remaining_disallowance")
    ]
      ?.map((val) => (isNaN(val) ? 0 : val))
      .reduce((acc, val) => acc + val, 0);
    if (sum !== 0) {
      error =
        sum > 0
          ? `Remaining disallowance (${Number(sum).toLocaleString()}) can't be greater than 0`
          : `Remaining disallowance (${Number(sum).toLocaleString()}) can't be less than 0`;
    }
    setIsErrorB(error);
  }, [disallowed_deductions_table_data]);

  useEffect(() => {
    const savedData = getGroupData();
    if (isReadOnlyOrViewPoA) {
      setupTableData(savedData);
      return;
    }
    if (poaIndex === -1) {
      toast.error(`could not find POA data`, { duration: 30 * 1000 }); // 30s
      return;
    }

    const poa = getReviewCalc3Values(
      savedData?.periods_of_account[poaIndex],
      savedData,
    );
    if (!poa) {
      console.error("calculations failed");
      return;
    }

    savedData.periods_of_account[poaIndex] = poa;
    setupTableData(savedData);
    setGroupData(savedData);
  }, []);

  const setupTableData = (calculatedData) => {
    setSavedData(calculatedData);
    const savedPOAData =
      calculatedData?.periods_of_account[poaIndex > -1 ? poaIndex : 0];
    setSavedPOAData(savedPOAData);

    const adjustedCaps = sortCaps(
      getIncludedAdjCaps(savedPOAData?.adjusted_caps),
    );
    const isReviewCalc3Saved = adjustedCaps?.some((cap) =>
      cap?.hasOwnProperty("disallowance_gbp"),
    );

    let disallowed_allocation_columns = [
      { title: "", width: 200 },
      { title: "Group Total", width: 200 },
    ];
    adjustedCaps?.forEach((company) => {
      const companyName = calculatedData?.companies?.filter(
        (c) => c?.company_ID === company?.company_ID,
      )[0]?.company_name;
      disallowed_allocation_columns?.push({ title: companyName, width: 200 });
    });

    set_disallowed_allocation_table_columns(disallowed_allocation_columns);

    const extractRowValue = (rowHeader, cap) => {
      let value;
      const disallowanceMetricsMap = {
        ntlr_disallowance: "cpd_non_trading_loan_relationships",
        ntdc_disallowance: "cpd_non_trading_derivatives",
        tlr_disallowance: "cpd_trading_loan_relationships",
        tdc_disallowance: "cpd_trading_derivatives",
        ofa_disallowance: "cpd_other_financing",
      };

      if (
        rowHeader.hasOwnProperty("default") &&
        !isReviewCalc3Saved &&
        !isReadOnlyOrViewPoA
      ) {
        return rowHeader.default;
      } else if (rowHeader?.type?.includes("blank")) {
        return "";
      } else if (rowHeader?.metric_id === "max_disallowance") {
        value =
          cap?.non_consenting_company === true
            ? cap?.non_consenting_max_disallowance_gbp
            : cap?.consenting_max_disallowance_gbp;
        return value;
      } else if (
        rowHeader?.metric_id === "disallowance_gbp" ||
        rowHeader?.metric_id?.includes("subtotal")
      ) {
        value = cap[rowHeader?.metric_id]?.value;
        return value;
      } else if (rowHeader?.metric_id === "disallowance") {
        value = Math.round((cap?.disallowance_gbp?.value ?? 0) * cap.fx_rate);
        return value;
      } else if (rowHeader?.type === "calculation") {
        value = rowHeader.calculation(cap);
        return value;
      } else if (cap?.hasOwnProperty(rowHeader?.metric_id)) {
        value = cap[rowHeader?.metric_id];
        return value;
      } else if (
        Object.keys(disallowanceMetricsMap).includes(rowHeader?.metric_id) &&
        isReadOnlyOrViewPoA
      ) {
        const metric = disallowanceMetricsMap[rowHeader?.metric_id];
        return cap[metric];
      } else {
        return "";
      }
    };

    const populateTableData = (tableRows, tableName) => {
      let tempTableDataBuilder = [];

      tableRows?.forEach((rowHeader) => {
        let tempRowDataBuilder = [];
        adjustedCaps?.forEach((cap) => {
          if (
            tableName === "orderOfDisallowedDeductions" &&
            !cap?.apply_s377_3_election &&
            !isReadOnlyOrViewPoA
          ) {
            cap = calculate_disallowance_subtotals(
              cap,
              Math.max(cap?.net_tax_interest_expense_gbp, 0),
              cap?.disallowance?.value,
              cap?.disallowance_gbp?.value,
            );
          }

          const rowValue = extractRowValue(rowHeader, cap);
          tempRowDataBuilder.push(rowValue);
        });
        tempTableDataBuilder.push(tempRowDataBuilder);
      });
      return tempTableDataBuilder;
    };

    let tempTableDataBuilder = populateTableData(
      disallowed_allocation_table_rows,
      "allocationOfDisallowances",
    );
    setDataInTheTable(tempTableDataBuilder);
    setRemainingDisallowanceForAllocation(
      tempTableDataBuilder,
      savedPOAData?.group_disallowance,
    );

    let netTaxExpenseIncomeTotal = tempTableDataBuilder[
      getDisallowedAllocationIndex("net_tax_interest_expense_gbp")
    ].reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    let disallowanceAllocation = tempTableDataBuilder[
      getDisallowedAllocationIndex("disallowance_gbp")
    ].reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    set_disallowed_allocation_table_data(tempTableDataBuilder);
    set_disallowed_allocation_totals_column([
      null,
      null,
      null,
      netTaxExpenseIncomeTotal,
      null,
      disallowanceAllocation,
    ]);
    let disallowed_deductions_table_columns = [{ title: "", width: 400 }];
    // change to use values from adjusted_caps
    adjustedCaps?.forEach((company) => {
      const companyName = calculatedData?.companies?.filter(
        (c) => c?.company_ID === company?.company_ID,
      )[0]["company_name"];
      disallowed_deductions_table_columns?.push({
        title: companyName,
        width: 200,
      });
    });

    set_disallowed_deductions_table_columns(
      disallowed_deductions_table_columns,
    );

    tempTableDataBuilder = populateTableData(
      disallowed_deductions_table_rows,
      "orderOfDisallowedDeductions",
    );
    const totalIndex = 17;
    tempTableDataBuilder[totalIndex]?.forEach((value, col) => {
      tempTableDataBuilder[totalIndex][col] =
        tempTableDataBuilder[getDisallowedDeductionsIndex("disallowance")][
          col
        ] -
        tempTableDataBuilder[getDisallowedDeductionsIndex("ntlr_disallowance")][
          col
        ] -
        tempTableDataBuilder[getDisallowedDeductionsIndex("ntdc_disallowance")][
          col
        ] -
        tempTableDataBuilder[getDisallowedDeductionsIndex("tlr_disallowance")][
          col
        ] -
        tempTableDataBuilder[getDisallowedDeductionsIndex("tdc_disallowance")][
          col
        ] -
        tempTableDataBuilder[getDisallowedDeductionsIndex("ofa_disallowance")][
          col
        ];
    });
    set_disallowed_deductions_table_data(tempTableDataBuilder);

    setIsLoading(false);
  };
  const getAllocationCellContent = useCallback(
    (col, row) => {
      const savedData = getGroupData();
      const metric = disallowed_allocation_table_rows[row];
      if (metric?.type === "blank") {
        return {
          kind: GridCellKind?.Text,
          allowOverlay: false,
          data: " ",
          displayData: " ",
          span: [0, savedData?.length],
        };
      }

      if (
        metric?.type === "header" ||
        metric?.type === "subheader" ||
        metric?.type === "note"
      ) {
        return {
          kind: GridCellKind?.Text,
          allowOverlay: false,
          data: metric?.display_name,
          displayData: metric?.display_name,
          span: [0, savedData?.length],
        };
      }

      // Metric name
      if (col === -2) {
        return {
          kind: GridCellKind?.Text,
          data: metric?.display_name,
          allowOverlay: false,
          displayData: metric?.display_name,
          allowWrapping: true,
        };
      }

      // Default values
      let cellType = GridCellKind?.Text;
      let value = disallowed_allocation_table_data[row][col] || 0;
      let displayValue = value ? String(value) : "0";
      let editable = metric?.hasOwnProperty("editable")
        ? metric?.editable
        : false;

      // Totals column
      if (col === -1) {
        if (metric?.type?.includes("number_total")) {
          return {
            kind: GridCellKind?.Number,
            data: disallowed_allocation_totals_column[row],
            displayData: disallowed_allocation_totals_column[row]
              ? Math.round(
                  disallowed_allocation_totals_column[row],
                )?.toLocaleString()
              : "0",
          };
        }
        return {
          kind: GridCellKind.Text,
          allowOverlay: false,
          data: " ",
          displayData: " ",
          span: [0, savedData?.length],
        };
      } else if (metric?.type?.includes("date")) {
        value = displayValue = value
          ? (processDateJS(value)?.format("DD/MM/YYYY") ?? "")
          : "";
      } else if (metric?.type?.includes("number")) {
        cellType = GridCellKind?.Number;

        displayValue =
          typeof value === "number" ? Math.round(value)?.toLocaleString() : "0";
      } else if (metric.type?.includes("boolean")) {
        cellType = GridCellKind.Boolean;
        if (value === 1 || value === true) {
          value = displayValue = true;
        } else if (value === 0 || value === false) {
          value = displayValue = false;
        } else {
          value = displayValue = false;
        }
      }
      return {
        kind: cellType,
        data: value,
        allowOverlay: editable,
        displayData: displayValue ?? 0,
        themeOverride: {
          ...(metric?.style === "bold" && { baseFontStyle: "600 15px" }),
          ...(editable ? { bgCell: "#FFFFFF" } : { bgCell: "#ffd5b7" }),
        },
      };
    },
    [
      disallowed_allocation_table_rows,
      disallowed_allocation_table_data,
      disallowed_allocation_totals_column,
    ],
  );

  const getDeductionsCellContent = useCallback(
    (col, row) => {
      const metric = disallowed_deductions_table_rows[row];
      if (metric?.type === "blank") {
        return {
          kind: GridCellKind.Text,
          allowOverlay: false,
          data: " ",
          displayData: " ",
          span: [0, savedData?.length],
        };
      }

      if (
        metric?.type === "header" ||
        metric?.type === "subheader" ||
        metric?.type === "note"
      ) {
        return {
          kind: GridCellKind.Text,
          allowOverlay: false,
          data: metric?.display_name,
          displayData: metric?.display_name,
          span: [0, savedData?.length],
        };
      }

      // Metric name
      if (col === -1) {
        return {
          kind: GridCellKind.Text,
          data: metric?.display_name,
          allowOverlay: false,
          displayData: metric?.display_name,
          allowWrapping: true,
        };
      }

      // set up default values
      let cellType = GridCellKind.Text;

      let value = disallowed_deductions_table_data[row][col];
      let displayValue = value ? String(value) : "";
      let editable = metric?.hasOwnProperty("editable")
        ? metric?.editable
        : false;

      // if cell is only editable on selection of a checkbox
      if (metric?.type?.includes("edit_number")) {
        editable =
          disallowed_deductions_table_data[
            getDisallowedDeductionsIndex("apply_s377_3_election")
          ][col];
        displayValue = value ? Math.round(value)?.toLocaleString() : "0";
      } else if (metric?.type?.includes("date")) {
        value = displayValue = value
          ? processDateJS(value)?.format("DD/MM/YYYY")
          : "";
      } else if (metric?.type?.includes("number")) {
        cellType = GridCellKind?.Number;
        displayValue = value ? Math?.round(value)?.toLocaleString() : "0";
      } else if (metric?.type?.includes("boolean")) {
        cellType = GridCellKind.Boolean;
        if (value === 1 || value === true) {
          value = displayValue = true;
        } else if (value === 0 || value === false) {
          value = displayValue = false;
        } else {
          value = displayValue = false;
        }
      } else if (metric?.type === "calculation") {
        displayValue = String(value);
      }

      return {
        kind: cellType,
        data: value,
        allowOverlay: editable,
        displayData: displayValue,
        themeOverride: {
          ...(metric?.style === "bold" && { baseFontStyle: "600 15px" }),
          ...(editable ? { bgCell: "#FFFFFF" } : { bgCell: "#ffd5b7" }),
        },
      };
    },
    [
      disallowed_deductions_table_rows,
      disallowed_deductions_table_data,
      disallowed_allocation_totals_column,
    ],
  );

  const [rowGrouping, setRowGrouping] = useState(() => ({
    groups: [
      {
        headerIndex: 0,
        isCollapsed: false,
      },
      {
        headerIndex: 10,
        isCollapsed: true,
        subGroups: [
          {
            headerIndex: 15,
            isCollapsed: false,
          },
          {
            headerIndex: 20,
            isCollapsed: false,
          },
        ],
      },
      {
        headerIndex: 30,
        isCollapsed: false,
      },
    ],
    height: 55,
    navigationBehavior: "block",
    selectionBehavior: "block-spanning",
    themeOverride: {
      bgCell: "rgba(0, 100, 255, 0.1)",
    },
  }));

  const save = async () => {
    if (isErrorA || isErrorB) {
      throw "There is an error. Please fix before proceeding...";
    }

    if (isReadOnlyOrViewPoA) {
      return;
    }

    setIsPosting(true);

    const new_caps = sortCaps(savedPOAData?.adjusted_caps)?.map(
      (cap, index) => {
        cap.non_consenting_company =
          !!disallowed_allocation_table_data[
            getDisallowedAllocationIndex("non_consenting_company")
          ][index];
        cap.apply_s377_3_election =
          disallowed_deductions_table_data[
            getDisallowedDeductionsIndex("apply_s377_3_election")
          ][index];

        // GBP Calculations
        cap.disallowance_gbp = {
          value:
            disallowed_allocation_table_data[
              getDisallowedAllocationIndex("disallowance_gbp")
            ][index],
          automatic_allocation:
            disallowed_allocation_table_data[
              getDisallowedAllocationIndex("disallowance_gbp")
            ][index] === Math.round(cap?.disallowance_automatic_allocation_gbp),
          reviewCalc3Saved: true,
        };
        cap.ntlr_disallowance_gbp = Math.round(
          disallowed_deductions_table_data[
            getDisallowedDeductionsIndex("ntlr_disallowance")
          ][index] / cap.fx_rate,
        );
        cap.ntdc_disallowance_gbp = Math.round(
          disallowed_deductions_table_data[
            getDisallowedDeductionsIndex("ntdc_disallowance")
          ][index] / cap.fx_rate,
        );
        cap.tlr_disallowance_gbp = Math.round(
          disallowed_deductions_table_data[
            getDisallowedDeductionsIndex("tlr_disallowance")
          ][index] / cap.fx_rate,
        );
        cap.tdc_disallowance_gbp = Math.round(
          disallowed_deductions_table_data[
            getDisallowedDeductionsIndex("tdc_disallowance")
          ][index] / cap.fx_rate,
        );
        cap.ofa_disallowance_gbp = Math.round(
          disallowed_deductions_table_data[
            getDisallowedDeductionsIndex("ofa_disallowance")
          ][index] / cap.fx_rate,
        );

        // LC Calculations
        cap.disallowance = {
          value:
            disallowed_deductions_table_data[
              getDisallowedDeductionsIndex("disallowance")
            ][index],
          automatic_allocation:
            disallowed_deductions_table_data[
              getDisallowedDeductionsIndex("disallowance")
            ][index] ===
            Math.round(
              cap?.disallowance_automatic_allocation_gbp * cap?.fx_rate,
            ),
          reviewCalc3Saved: true,
        };
        cap.ntlr_disallowance =
          disallowed_deductions_table_data[
            getDisallowedDeductionsIndex("ntlr_disallowance")
          ][index];
        cap.ntdc_disallowance =
          disallowed_deductions_table_data[
            getDisallowedDeductionsIndex("ntdc_disallowance")
          ][index];
        cap.tlr_disallowance =
          disallowed_deductions_table_data[
            getDisallowedDeductionsIndex("tlr_disallowance")
          ][index];
        cap.tdc_disallowance =
          disallowed_deductions_table_data[
            getDisallowedDeductionsIndex("tdc_disallowance")
          ][index];
        cap.ofa_disallowance =
          disallowed_deductions_table_data[
            getDisallowedDeductionsIndex("ofa_disallowance")
          ][index];

        return cap;
      },
    );
    const canProgressCalc = canProgressCalculation();

    let newPOA = {
      ...savedPOAData,
      ...canProgressCalc,
      adjusted_caps: new_caps,
    };

    if (poaIndex === -1) {
      throw `could not find POA data`;
    }

    savedData.periods_of_account[poaIndex] = newPOA;
    savedData.company_accounting_periods =
      savedData.company_accounting_periods.map((ap) => {
        const correspondingCap = new_caps.find(
          (cap) =>
            cap.company_ID === ap.company_ID &&
            processDateJS(cap.end_date)?.isSame(processDateJS(ap.end_date)) &&
            processDateJS(cap.start_date)?.isSame(processDateJS(ap.start_date)),
        );
        return correspondingCap
          ? {
              ...ap,
              non_consenting_company: correspondingCap.non_consenting_company,
            }
          : ap;
      });

    const newJson = savedData;

    const { company_accounting_periods, furthest_page_visited } = newJson;
    const requestBody = {
      id: getGroupID(),
      poa: newPOA,
      poaIndex: getPoaIndex(),
      companyAccountingPeriods: company_accounting_periods,
      furthest_page_visited,
    };
    const promise = sqlService.postReviewCalc3(tenantId, requestBody);

    const onSuccess = () => {
      setGroupData(newJson);
      setIsPosting(false);
    };
    const onError = () => {
      setIsPosting(false);
    };
    return toast.save(promise, onSuccess, onError);
  };

  const disallowanceError = (
    table,
    valueRow,
    maxRow,
    setError,
    hasTotalRow,
    ntie,
  ) => {
    let hasError;
    for (const [index, value] of table[valueRow]?.entries() || []) {
      if (!hasTotalRow || index > 0) {
        const max = ntie
          ? Math.min(
              Math.round(table[maxRow][index] + 1),
              Math.round(table[ntie][index]),
            )
          : Math.round(table[maxRow][index] + 1);
        if (value < 0 || value > Math.max(max, 0)) {
          hasError = { max, value };
          break;
        }
      }
    }
    if (hasError) {
      setError(
        `Disallowance allocation (${hasError.value}) must be a number greater than 0 and less than the disallowance allocation limit (${hasError.max})`,
      );
    } else {
      setError("");
    }
  };

  const applyAutomaticAllocation = () => {
    let temp_disallowed_allocation_data = [...disallowed_allocation_table_data];
    let temp_disallowed_deductions_data = [...disallowed_deductions_table_data];

    temp_disallowed_allocation_data[
      getDisallowedAllocationIndex("disallowance_gbp")
    ]?.forEach((cell, index) => {
      const cap = savedPOAData?.adjusted_caps?.find(
        (cap) =>
          cap?.company_name ===
            disallowed_allocation_table_columns[index + 2]?.title &&
          processDateJS(cap?.start_date)?.isSame(
            processDateJS(
              temp_disallowed_allocation_data[
                getDisallowedAllocationIndex("start_date")
              ][index],
            ),
          ),
      );
      if (!cap) return;

      temp_disallowed_allocation_data[
        getDisallowedAllocationIndex("disallowance_gbp")
      ][index] = Math.round(cap?.disallowance_automatic_allocation_gbp);
      temp_disallowed_deductions_data[
        getDisallowedDeductionsIndex("disallowance")
      ][index] = Math.round(cap?.disallowance_automatic_allocation_gbp);

      const newSubtotals = calculate_disallowance_subtotals(
        cap,
        Math.max(cap?.net_tax_interest_expense_gbp, 0),
        Math.round(cap?.disallowance_automatic_allocation_gbp * cap?.fx_rate),
        Math.round(cap?.disallowance_automatic_allocation_gbp),
      );
      if (
        !temp_disallowed_allocation_data[
          getDisallowedAllocationIndex("net_tax_interest_expense_gbp")
        ][index]
      ) {
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("ntlr_disallowance")
        ][index] = newSubtotals?.ntlr_disallowance;
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("ntdc_disallowance")
        ][index] = newSubtotals?.ntdc_disallowance;
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("tlr_disallowance")
        ][index] = newSubtotals?.tlr_disallowance;
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("tdc_disallowance")
        ][index] = newSubtotals?.tdc_disallowance;
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("ofa_disallowance")
        ][index] = newSubtotals?.ofa_disallowance;
      }
    });

    set_disallowed_allocation_table_data(temp_disallowed_allocation_data);
    set_disallowed_deductions_table_data(temp_disallowed_deductions_data);
    let tmp_disallowed_allocation_totals_column = [
      ...temp_disallowed_allocation_data,
    ];
    disallowed_allocation_table_rows.forEach((metric, index) => {
      if (metric.type !== "number_total") return;

      tmp_disallowed_allocation_totals_column[index] =
        disallowed_allocation_table_data[index].reduce(
          (acc, val) => acc + val,
          0,
        );
    });
    setRemainingDisallowance(
      Number(savedPOAData?.group_disallowance).toLocaleString(),
    );

    onAllocationOfDisallowancesEdited(temp_disallowed_allocation_data);
  };

  const resetAllocation = () => {
    let temp_disallowed_allocation_data = [...disallowed_allocation_table_data];
    let temp_disallowed_deductions_data = [...disallowed_deductions_table_data];

    temp_disallowed_allocation_data[
      getDisallowedAllocationIndex("disallowance_gbp")
    ]?.forEach((cell, index) => {
      const cap = savedPOAData?.adjusted_caps?.find(
        (cap) =>
          cap?.company_name ===
            disallowed_allocation_table_columns[index + 2]?.title &&
          processDateJS(cap?.start_date)?.isSame(
            processDateJS(
              temp_disallowed_allocation_data[
                getDisallowedAllocationIndex("start_date")
              ][index],
            ),
          ),
      );
      if (!cap) return;

      temp_disallowed_allocation_data[
        getDisallowedAllocationIndex("disallowance_gbp")
      ][index] = 0;

      if (
        !temp_disallowed_allocation_data[
          getDisallowedAllocationIndex("net_tax_interest_expense_gbp")
        ][index]
      ) {
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("disallowance")
        ][index] = 0;
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("ntlr_disallowance")
        ][index] = 0;
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("ntdc_disallowance")
        ][index] = 0;
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("tlr_disallowance")
        ][index] = 0;
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("tdc_disallowance")
        ][index] = 0;
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("ofa_disallowance")
        ][index] = 0;
      }
    });

    set_disallowed_allocation_table_data(temp_disallowed_allocation_data);
    set_disallowed_deductions_table_data(temp_disallowed_deductions_data);
    onAllocationOfDisallowancesEdited(temp_disallowed_allocation_data);
  };

  const onAllocationsEdit = (col, row, value) => {
    const metric = disallowed_allocation_table_rows[row];

    if (!metric) {
      return;
    }

    if (metric?.type === "date") {
      const dayjsDate = dayjs.utc(value, "DD/MM/YYYY", true);
      if (!dayjsDate?.isValid()) {
        toast.error("Date must be entered in dd/mm/yyyy format");
        return;
      }
      value = processDate(dayjsDate);
    }

    const cap = savedPOAData?.adjusted_caps?.find(
      (cap) =>
        cap?.company_name ===
          disallowed_deductions_table_columns[col + 1]?.title &&
        processDateJS(cap?.start_date)?.isSame(
          processDateJS(
            disallowed_deductions_table_data[
              getDisallowedDeductionsIndex("start_date")
            ][col],
          ),
        ),
    );

    let temp_disallowed_allocation_data = [...disallowed_allocation_table_data];
    temp_disallowed_allocation_data[row][col] = isNaN(value)
      ? 0
      : Math.round(value);
    if (metric?.metric_id === "non_consenting_company") {
      temp_disallowed_allocation_data[
        getDisallowedAllocationIndex("max_disallowance")
      ][col] = temp_disallowed_allocation_data[
        getDisallowedAllocationIndex("non_consenting_company")
      ][col]
        ? cap?.non_consenting_max_disallowance_gbp
        : cap?.consenting_max_disallowance_gbp;
    }

    set_disallowed_allocation_table_data(temp_disallowed_allocation_data);
    onAllocationOfDisallowancesEdited(temp_disallowed_allocation_data);
  };

  const onDisallowancesEdit = (col, row, value) => {
    const metric = disallowed_deductions_table_rows[row];
    if (!metric) {
      return;
    }

    if (metric?.type === "date") {
      const dayjsDate = processDateJS(value)?.format("DD/MM/YYYY");
      if (!dayjsDate?.isValid()) {
        toast.error("Date must be entered in dd/mm/yyyy format");
        return;
      }
      value = dayjsDate?.toDate();
    }
    // don't allow editing of non consenting company
    if (metric?.metric_id === "non_consenting_company") {
      value = !value;
    }
    let temp_disallowed_deductions_data = [...disallowed_deductions_table_data];

    temp_disallowed_deductions_data[row][col] = isNaN(value)
      ? 0
      : Math?.round(value);

    let totalCalculated = false;

    if (metric?.metric_id === "apply_s377_3_election") {
      temp_disallowed_deductions_data[row][col] = value;
      if (value === false) {
        const cap = savedPOAData?.adjusted_caps?.find(
          (cap) =>
            cap?.company_name ===
              disallowed_deductions_table_columns[col + 1]?.title &&
            processDateJS(cap?.start_date)?.isSame(
              processDateJS(
                temp_disallowed_deductions_data[
                  getDisallowedDeductionsIndex("start_date")
                ][col],
              ),
            ),
        );
        if (cap) {
          const newSubtotals = calculate_disallowance_subtotals(
            cap,
            Math.max(cap?.net_tax_interest_expense_gbp, 0),
            temp_disallowed_deductions_data[
              getDisallowedDeductionsIndex("disallowance")
            ][col],
            temp_disallowed_deductions_data[
              getDisallowedDeductionsIndex("disallowance_gbp")
            ][col],
          );
          temp_disallowed_deductions_data[
            getDisallowedDeductionsIndex("ntlr_disallowance")
          ][col] = newSubtotals?.ntlr_disallowance;
          temp_disallowed_deductions_data[
            getDisallowedDeductionsIndex("ntdc_disallowance")
          ][col] = newSubtotals?.ntdc_disallowance;
          temp_disallowed_deductions_data[
            getDisallowedDeductionsIndex("tlr_disallowance")
          ][col] = newSubtotals?.tlr_disallowance;
          temp_disallowed_deductions_data[
            getDisallowedDeductionsIndex("tdc_disallowance")
          ][col] = newSubtotals?.tdc_disallowance;
          temp_disallowed_deductions_data[
            getDisallowedDeductionsIndex("ofa_disallowance")
          ][col] = newSubtotals?.ofa_disallowance;

          temp_disallowed_deductions_data[
            getDisallowedDeductionsIndex("remaining_disallowance")
          ][col] =
            temp_disallowed_deductions_data[
              getDisallowedDeductionsIndex("disallowance")
            ][col] -
            newSubtotals?.ntlr_disallowance -
            newSubtotals?.ntdc_disallowance -
            newSubtotals?.tlr_disallowance -
            newSubtotals?.tdc_disallowance -
            newSubtotals?.ofa_disallowance;

          totalCalculated = true;
        }
      }
    }

    if (!totalCalculated) {
      temp_disallowed_deductions_data[
        getDisallowedDeductionsIndex("remaining_disallowance")
      ][col] =
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("disallowance")
        ][col] -
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("ntlr_disallowance")
        ][col] -
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("ntdc_disallowance")
        ][col] -
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("tlr_disallowance")
        ][col] -
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("tdc_disallowance")
        ][col] -
        temp_disallowed_deductions_data[
          getDisallowedDeductionsIndex("ofa_disallowance")
        ][col];
    }

    set_disallowed_deductions_table_data(temp_disallowed_deductions_data);
  };

  const getMetricTheme = (metric) => {
    if (!metric) {
      return {};
    }

    if (metric.type === "blank") {
      return {
        bgCell: "#F4F3F7",
      };
    }

    if (metric.type === "header") {
      return {
        baseFontStyle: "600 18px",
      };
    }

    if (metric.type === "subheader") {
      return {
        baseFontStyle: "600 15px",
      };
    }

    if (metric.type === "note") {
      return {
        baseFontStyle: "600 12px",
      };
    }
  };

  const shouldDisable = () => {
    if (isReadOnlyOrViewPoA) return false;

    if (
      dataInTheTable[getDisallowedAllocationIndex("max_disallowance")] &&
      dataInTheTable[getDisallowedAllocationIndex("disallowance_gbp")]
    ) {
      for (
        let i = 0;
        i <
        dataInTheTable[getDisallowedAllocationIndex("max_disallowance")].length;
        i++
      ) {
        if (
          dataInTheTable[getDisallowedAllocationIndex("max_disallowance")][i] <
          dataInTheTable[getDisallowedAllocationIndex("disallowance_gbp")][i]
        ) {
          return true;
        }
      }
    }
  };

  if (isLoading)
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      ></Loading>
    );
  return (
    <div className="ap-container">
      <div className="ap-container mt-4">
        <CompanyPanel />
      </div>
      <Panel className="mt-4">
        <RevCalcHeader groupname="Review Calculations -  Allocation of disallowances" />
        <ReviewCalcNav currentStep={2} />
        <h2>Disallowance of Tax Interest Expense</h2>
        <p>
          The Group disallowance is calculated by comparing the group's
          aggregate net tax-interest expense to the group's interest capacity.
          Where the expense exceeds the capacity, this results in a
          disallowance.
        </p>
        <div className="col-4">
          Aggregate Net-Tax Interest Expense / (Income):{" "}
          <strong>
            {Math.round(
              savedPOAData?.aggregate_net_tax_interest_expense_income,
            )?.toLocaleString()}
          </strong>
        </div>
        <div className="col-4">
          Interest Capacity:{" "}
          <strong>
            {Math.round(savedPOAData?.group_interest_capacity).toLocaleString()}
          </strong>
        </div>
        <div className="col-4" style={{ padding: "0em 0em 1em 0em" }}>
          Disallowance:{" "}
          <strong>
            {Math.round(
              savedPOAData?.group_disallowance,
            )?.toLocaleString()}{" "}
          </strong>
        </div>
        <h2>Allocation of disallowances</h2>
        <p className="mb-0">
          Where a disallowance is calculated in respect of a period of account,
          it is necessary to allocate the disallowance to one or more group
          companies and reflect this in the relevant tax computations.
          Disallowed amounts can be freely allocated to companies within the
          group, subject to the following restrictions:
        </p>
        <ul type="a">
          <li>
            Disallowances can only be allocated to companies which have an
            overall net tax interest expense for the period. No amount can be
            allocated to companies which have net tax interest income;
          </li>
          <li>
            The maximum amount which can be allocated to a company is the net
            tax interest expense arising in the relevant accounting period;
          </li>
          <li>
            If the company is a non-consenting company, the maximum that can be
            allocated is the company’s pro-rata share of the total disallowed
            amount; and
          </li>
          <li>
            If there is no active reporting company nomination, disallowed
            amounts can only be allocated on a pro-rata basis.
          </li>
        </ul>
        <p className="err">{isErrorA}</p>
        <div
          id="portal"
          style={{ position: "fixed", left: 0, top: 0, zIndex: 9999 }}
        />
        <RoundedBorder>
          <DataEditor
            columns={disallowed_allocation_table_columns}
            getCellContent={([col, row]) =>
              getAllocationCellContent((col = col - 2), row)
            }
            rows={disallowed_allocation_table_rows?.length}
            freezeColumns={1}
            smoothScrollX
            rowGrouping={rowGrouping}
            onCellEdited={([col, row], event) =>
              onAllocationsEdit((col = col - 2), row, event?.data)
            }
            getRowThemeOverride={(i) =>
              getMetricTheme(disallowed_allocation_table_rows[i])
            }
          />
        </RoundedBorder>
        <div className="row">
          <div className="col-3">Remaining disallowance to be allocated</div>
          <InputNumber
            key={remainingDisallowance?.replace(/,/g, "")}
            className="col-auto"
            value={remainingDisallowance}
            readonly
          ></InputNumber>
          <ButtonGroup className="col-2" compact={false}>
            <Button onClick={applyAutomaticAllocation}>
              Apply Automatic Allocation
            </Button>
            <Button onClick={resetAllocation}>Reset</Button>
          </ButtonGroup>
        </div>
        <h2>Order of disallowed deductions</h2>
        <div>
          By default, tax-interest expense amounts must be left out of account
          in the following order:
          <ol>
            <li>Non-trading debits in respect of loan relationships</li>
            <li>Non-trading debits in respect of derivative contracts</li>
            <li>
              Debits in respect of loan relationships treated as expenses of
              trade
            </li>
            <li>
              Debits in respect of derivative contracts treated as expenses of
              trade
            </li>
            <li>
              Finance leases, debt factoring and service concession arrangements
            </li>
          </ol>
          A company may make an election for this order not to apply for an
          accounting period, in which case the company must specify the
          particular tax-interest expense amounts that are to be left out of
          account:
        </div>
        <p className="err">{isErrorB}</p>
        <div
          id="portal"
          style={{ position: "fixed", left: 0, top: 0, zIndex: 9999 }}
        />
        <RoundedBorder>
          <DataEditor
            columns={disallowed_deductions_table_columns}
            getCellContent={([col, row]) =>
              getDeductionsCellContent((col = col - 1), row)
            }
            rows={disallowed_deductions_table_rows?.length}
            freezeColumns={1}
            smoothScrollX
            rowGrouping={rowGrouping}
            onCellEdited={([col, row], event) =>
              onDisallowancesEdit((col = col - 1), row, event.data)
            }
            getRowThemeOverride={(i) =>
              getMetricTheme(disallowed_deductions_table_rows[i])
            }
          />
        </RoundedBorder>

        <div className="container-fluid mt-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <BackButton disabled={isPosting} />
            </div>
            <div className="d-flex flex-column align-items-end">
              <Tooltip
                disabled={
                  !(
                    isErrorA ||
                    isErrorB ||
                    shouldDisable() ||
                    remainingDisallowanceError
                  )
                }
                position="top"
                style={{ maxWidth: "50rem" }}
                content={() =>
                  `${isErrorA || isErrorB || remainingDisallowanceError}`
                }
              >
                <div className="d-flex gap-2">
                  <Button
                    onClick={save}
                    hiddenInReadonly
                    loading={isPosting}
                    disabled={
                      isErrorA ||
                      isErrorB ||
                      shouldDisable() ||
                      remainingDisallowanceError
                    }
                    kind="secondary"
                  >
                    Save
                  </Button>
                  <div className="col-1">
                    <NextButton
                      loading={isPosting}
                      disabled={
                        isErrorA ||
                        isErrorB ||
                        shouldDisable() ||
                        remainingDisallowanceError ||
                        disableViewModeProgress
                      }
                      preNavigation={save}
                    >
                      Next
                    </NextButton>
                  </div>
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
      </Panel>
    </div>
  );
};

export default ReviewCalc3;
